import { useEffect } from 'react';

import { ensurePostHog } from '@/lib/analytics/posthog-client';

function shouldDeferPostHog(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname.startsWith('/help') ||
    pathname.startsWith('/explore') ||
    pathname.startsWith('/guides') ||
    pathname.startsWith('/terms') ||
    pathname.startsWith('/privacy') ||
    pathname.startsWith('/legal')
  );
}

/**
 * Mount-time kick for PostHog. All loading, gating, and init config live in
 * `lib/analytics/posthog-client.ts :: ensurePostHog`, shared with
 * `funnel.ts` so an early funnel event and this provider can no longer race
 * each other — whichever runs first initializes, the other reuses it.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

    const start = () => {
      void ensurePostHog().catch(() => undefined);
    };

    if (!shouldDeferPostHog(pathname)) {
      start();
      return;
    }

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(start, { timeout: 4000 });
      return () => window.cancelIdleCallback(handle);
    }

    const timeout = window.setTimeout(start, 2500);
    return () => window.clearTimeout(timeout);
  }, []);

  return <>{children}</>;
}
